/**
 * SKOP AI Analysis Service
 *
 * Provides AI-powered token analysis using Claude Sonnet.
 * Features:
 * - Cached teasers (shared across users)
 * - Full analysis (unlimited for all users)
 * - Chat follow-up capability
 */

import { type TokenAnalysis } from './api';

// ============================================================================
// Types
// ============================================================================

export interface AITeaser {
  summary: string;
  confidence: 'high' | 'medium' | 'low';
  sentiment: 'bullish' | 'bearish' | 'neutral' | 'warning';
  cachedAt: number;
}

export interface AIFullAnalysis {
  id: string;
  tokenAddress: string;
  summary: string;
  confidence: 'high' | 'medium' | 'low';
  sentiment: 'bullish' | 'bearish' | 'neutral' | 'warning';

  // Detailed sections
  riskAssessment: {
    score: number;
    reasoning: string;
    keyFactors: string[];
  };

  creatorAnalysis: {
    trustLevel: 'trusted' | 'neutral' | 'suspicious' | 'dangerous';
    reasoning: string;
    redFlags: string[];
    greenFlags: string[];
  };

  marketAnalysis: {
    volumeAuthenticity: 'organic' | 'mixed' | 'suspicious';
    holderDistribution: 'healthy' | 'concentrated' | 'concerning';
    reasoning: string;
  };

  recommendation: {
    action: 'strong_buy' | 'buy' | 'hold' | 'avoid' | 'strong_avoid';
    reasoning: string;
    suggestedPosition?: string;
    stopLoss?: string;
  };

  additionalInsights: string[];

  generatedAt: number;
}

export interface AIChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

export interface AIChatSession {
  tokenAddress: string;
  messages: AIChatMessage[];
  createdAt: number;
}

// ============================================================================
// Configuration
// ============================================================================

const TEASER_CACHE_KEY = 'skop_ai_teaser_cache';
const FULL_ANALYSIS_CACHE_KEY = 'skop_ai_full_cache';
const USAGE_KEY = 'skop_ai_usage';
const CHAT_SESSION_KEY = 'skop_ai_chat';

const TEASER_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
const FULL_ANALYSIS_CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

// AI limits per day (all users, no tier gating)
export const AI_LIMITS = {
  fullAnalysis: -1, // Unlimited
  chatMessages: -1, // Unlimited
};

// ============================================================================
// Usage Tracking
// ============================================================================

interface DailyAIUsage {
  date: string;
  fullAnalysisCount: number;
  chatMessageCount: number;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0];
}

export function getAIUsage(walletAddress: string): DailyAIUsage {
  try {
    const today = getTodayString();
    const stored = localStorage.getItem(`${USAGE_KEY}_${walletAddress}_${today}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}

  return {
    date: getTodayString(),
    fullAnalysisCount: 0,
    chatMessageCount: 0,
  };
}

function saveAIUsage(walletAddress: string, usage: DailyAIUsage): void {
  localStorage.setItem(
    `${USAGE_KEY}_${walletAddress}_${usage.date}`,
    JSON.stringify(usage)
  );
}

export function getRemainingAIUsage(walletAddress: string | null): {
  fullAnalysis: number;
  chatMessages: number;
} {
  if (!walletAddress) {
    return { fullAnalysis: 0, chatMessages: 0 };
  }

  const usage = getAIUsage(walletAddress);

  return {
    fullAnalysis: AI_LIMITS.fullAnalysis === -1 ? -1 : Math.max(0, AI_LIMITS.fullAnalysis - usage.fullAnalysisCount),
    chatMessages: AI_LIMITS.chatMessages === -1 ? -1 : Math.max(0, AI_LIMITS.chatMessages - usage.chatMessageCount),
  };
}

function trackFullAnalysis(walletAddress: string): boolean {
  const remaining = getRemainingAIUsage(walletAddress);
  if (remaining.fullAnalysis === 0) {
    return false; // Limit reached
  }

  const usage = getAIUsage(walletAddress);
  usage.fullAnalysisCount += 1;
  saveAIUsage(walletAddress, usage);
  return true;
}

function trackChatMessage(walletAddress: string): boolean {
  const remaining = getRemainingAIUsage(walletAddress);
  if (remaining.chatMessages === 0) {
    return false; // Limit reached
  }

  const usage = getAIUsage(walletAddress);
  usage.chatMessageCount += 1;
  saveAIUsage(walletAddress, usage);
  return true;
}

// ============================================================================
// Teaser Cache (Shared across all users)
// ============================================================================

function getCachedTeaser(tokenAddress: string): AITeaser | null {
  try {
    const stored = localStorage.getItem(`${TEASER_CACHE_KEY}_${tokenAddress}`);
    if (!stored) return null;

    const teaser: AITeaser = JSON.parse(stored);
    if (Date.now() - teaser.cachedAt > TEASER_CACHE_DURATION) {
      localStorage.removeItem(`${TEASER_CACHE_KEY}_${tokenAddress}`);
      return null;
    }

    return teaser;
  } catch {
    return null;
  }
}

function cacheTeaser(tokenAddress: string, teaser: AITeaser): void {
  localStorage.setItem(
    `${TEASER_CACHE_KEY}_${tokenAddress}`,
    JSON.stringify(teaser)
  );
}

// ============================================================================
// Full Analysis Cache
// ============================================================================

function getCachedFullAnalysis(tokenAddress: string): AIFullAnalysis | null {
  try {
    const stored = localStorage.getItem(`${FULL_ANALYSIS_CACHE_KEY}_${tokenAddress}`);
    if (!stored) return null;

    const analysis: AIFullAnalysis = JSON.parse(stored);
    if (Date.now() - analysis.generatedAt > FULL_ANALYSIS_CACHE_DURATION) {
      localStorage.removeItem(`${FULL_ANALYSIS_CACHE_KEY}_${tokenAddress}`);
      return null;
    }

    return analysis;
  } catch {
    return null;
  }
}

function cacheFullAnalysis(tokenAddress: string, analysis: AIFullAnalysis): void {
  localStorage.setItem(
    `${FULL_ANALYSIS_CACHE_KEY}_${tokenAddress}`,
    JSON.stringify(analysis)
  );
}

// ============================================================================
// AI Analysis Generation
// ============================================================================

/**
 * Generate AI teaser for a token
 * This is cached and shared across all users
 */
export async function getAITeaser(token: TokenAnalysis): Promise<AITeaser> {
  // Check cache first
  const cached = getCachedTeaser(token.address);
  if (cached) {
    return cached;
  }

  // In production, this would call Claude API
  // For now, generate intelligent mock based on token data
  const teaser = await generateMockTeaser(token);

  cacheTeaser(token.address, teaser);
  return teaser;
}

/**
 * Generate full AI analysis for a token
 * Requires authentication and tracks usage
 */
export async function getFullAIAnalysis(
  token: TokenAnalysis,
  walletAddress: string
): Promise<AIFullAnalysis | { error: string }> {
  // Check cache first (doesn't count against limit)
  const cached = getCachedFullAnalysis(token.address);
  if (cached) {
    return cached;
  }

  // Check limits
  const remaining = getRemainingAIUsage(walletAddress);
  if (remaining.fullAnalysis === 0) {
    return {
      error: 'Daily limit reached. Try again tomorrow.',
    };
  }

  // Track usage
  trackFullAnalysis(walletAddress);

  // In production, this would call Claude API
  const analysis = await generateMockFullAnalysis(token);

  cacheFullAnalysis(token.address, analysis);
  return analysis;
}

/**
 * Send a chat message about a token
 */
export async function sendAIChatMessage(
  tokenAddress: string,
  message: string,
  walletAddress: string
): Promise<{ response: string } | { error: string }> {
  const remaining = getRemainingAIUsage(walletAddress);

  if (remaining.chatMessages === 0) {
    return {
      error: 'Daily chat limit reached. Try again tomorrow.',
    };
  }

  // Track usage
  trackChatMessage(walletAddress);

  // Get or create chat session
  const session = getChatSession(tokenAddress, walletAddress);

  // Add user message
  session.messages.push({
    role: 'user',
    content: message,
    timestamp: Date.now(),
  });

  // In production, this would call Claude API with conversation history
  const response = await generateMockChatResponse(message, tokenAddress);

  // Add assistant message
  session.messages.push({
    role: 'assistant',
    content: response,
    timestamp: Date.now(),
  });

  saveChatSession(tokenAddress, walletAddress, session);

  return { response };
}

// ============================================================================
// Chat Session Management
// ============================================================================

function getChatSession(tokenAddress: string, walletAddress: string): AIChatSession {
  try {
    const stored = localStorage.getItem(`${CHAT_SESSION_KEY}_${walletAddress}_${tokenAddress}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {}

  return {
    tokenAddress,
    messages: [],
    createdAt: Date.now(),
  };
}

function saveChatSession(tokenAddress: string, walletAddress: string, session: AIChatSession): void {
  localStorage.setItem(
    `${CHAT_SESSION_KEY}_${walletAddress}_${tokenAddress}`,
    JSON.stringify(session)
  );
}

export function getExistingChatSession(tokenAddress: string, walletAddress: string): AIChatSession | null {
  try {
    const stored = localStorage.getItem(`${CHAT_SESSION_KEY}_${walletAddress}_${tokenAddress}`);
    if (stored) {
      const session = JSON.parse(stored);
      if (session.messages.length > 0) {
        return session;
      }
    }
  } catch {}
  return null;
}

export function clearChatSession(tokenAddress: string, walletAddress: string): void {
  localStorage.removeItem(`${CHAT_SESSION_KEY}_${walletAddress}_${tokenAddress}`);
}

// ============================================================================
// Mock AI Responses (Replace with real Claude API in production)
// ============================================================================

async function generateMockTeaser(token: TokenAnalysis): Promise<AITeaser> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  const { riskScore, top5HolderPercent, warnings, bundledBuys } = token;

  let summary = '';
  let confidence: 'high' | 'medium' | 'low' = 'medium';
  let sentiment: 'bullish' | 'bearish' | 'neutral' | 'warning' = 'neutral';

  // Generate contextual summary based on token data
  if (riskScore >= 75) {
    sentiment = 'warning';
    confidence = 'high';

    if (bundledBuys) {
      summary = `High-risk token. Bundled buys detected at launch indicating coordinated insider activity. Funding patterns match known scam wallets with 87% confidence.`;
    } else if (top5HolderPercent > 60) {
      summary = `Extreme holder concentration detected. Top 5 wallets control ${top5HolderPercent.toFixed(0)}% of supply. Dump risk is elevated.`;
    } else {
      summary = `Multiple red flags detected: ${warnings.slice(0, 2).map(w => w.toLowerCase()).join(', ')}. Exercise extreme caution.`;
    }
  } else if (riskScore >= 50) {
    sentiment = 'bearish';
    confidence = 'medium';

    if (warnings.length > 0) {
      summary = `Moderate concerns: ${warnings[0]}. Volume patterns suggest ${Math.random() > 0.5 ? 'some wash trading activity' : 'mixed organic/inorganic interest'}.`;
    } else {
      summary = `Mixed signals. Creator history is clean but holder distribution and trading patterns warrant monitoring.`;
    }
  } else if (riskScore >= 25) {
    sentiment = 'neutral';
    confidence = 'medium';

    summary = `Relatively healthy metrics. Creator has no rug history, holder distribution is reasonable. Watch for any sudden concentration changes.`;
  } else {
    sentiment = 'bullish';
    confidence = 'high';

    summary = `Low-risk profile. Organic trading patterns, healthy holder distribution, clean creator history. Fundamentals look solid for a memecoin.`;
  }

  return {
    summary,
    confidence,
    sentiment,
    cachedAt: Date.now(),
  };
}

async function generateMockFullAnalysis(token: TokenAnalysis): Promise<AIFullAnalysis> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1500));

  const { riskScore, top5HolderPercent, warnings, graduated, bundledBuys, creatorTokenCount } = token;
  const priceChange = token.priceChange24h ?? 0;

  // Determine overall sentiment
  let sentiment: 'bullish' | 'bearish' | 'neutral' | 'warning' = 'neutral';
  let confidence: 'high' | 'medium' | 'low' = 'medium';
  let action: 'strong_buy' | 'buy' | 'hold' | 'avoid' | 'strong_avoid' = 'hold';

  if (riskScore >= 75) {
    sentiment = 'warning';
    action = 'strong_avoid';
    confidence = 'high';
  } else if (riskScore >= 50) {
    sentiment = 'bearish';
    action = 'avoid';
  } else if (riskScore >= 25) {
    sentiment = 'neutral';
    action = 'hold';
  } else {
    sentiment = 'bullish';
    action = riskScore < 15 ? 'strong_buy' : 'buy';
    confidence = 'high';
  }

  // Build red and green flags
  const redFlags: string[] = [];
  const greenFlags: string[] = [];

  if (bundledBuys) redFlags.push('Bundled buys detected at launch');
  if (top5HolderPercent > 50) redFlags.push(`High concentration: top 5 hold ${top5HolderPercent.toFixed(0)}%`);
  if (warnings.some(w => w.toLowerCase().includes('critical') || w.toLowerCase().includes('rug'))) redFlags.push('Critical warnings present');
  if (!graduated) redFlags.push('Not yet graduated - liquidity risk');

  if (!bundledBuys) greenFlags.push('No bundled buys detected');
  if (top5HolderPercent < 30) greenFlags.push('Healthy holder distribution');
  if (graduated) greenFlags.push('Graduated with locked liquidity');
  if (warnings.length === 0) greenFlags.push('No warnings detected');

  return {
    id: `analysis_${Date.now()}`,
    tokenAddress: token.address,
    summary: `Comprehensive analysis of ${token.name} (${token.symbol}) reveals ${sentiment === 'warning' ? 'significant risks' : sentiment === 'bullish' ? 'positive indicators' : 'mixed signals'} requiring ${confidence} confidence assessment.`,
    confidence,
    sentiment,

    riskAssessment: {
      score: riskScore,
      reasoning: `The risk score of ${riskScore}/100 reflects ${riskScore >= 50 ? 'elevated concerns' : 'manageable risk levels'} based on creator history, holder distribution, and on-chain patterns. ${warnings.length > 0 ? `Key concerns include: ${warnings.slice(0, 2).join('; ')}.` : 'No major red flags detected in standard metrics.'}`,
      keyFactors: [
        `Creator token count: ${creatorTokenCount}`,
        `Bundled buys: ${bundledBuys ? 'Yes' : 'No'}`,
        `Holder concentration: ${top5HolderPercent.toFixed(1)}% in top 5`,
        `Warning count: ${warnings.length}`,
      ],
    },

    creatorAnalysis: {
      trustLevel: bundledBuys ? 'suspicious' : creatorTokenCount > 10 ? 'neutral' : 'trusted',
      reasoning: bundledBuys
        ? `Bundled purchases detected at token launch, indicating coordinated insider activity. This pattern is commonly associated with rug pulls.`
        : creatorTokenCount > 5
        ? `Serial launcher with ${creatorTokenCount} tokens. High volume of launches suggests possible opportunistic behavior rather than committed development.`
        : `Clean history with ${creatorTokenCount} previous token(s). No suspicious patterns detected. Creator appears to be relatively new or selective.`,
      redFlags,
      greenFlags,
    },

    marketAnalysis: {
      volumeAuthenticity: riskScore > 60 ? 'suspicious' : riskScore > 30 ? 'mixed' : 'organic',
      holderDistribution: top5HolderPercent > 50 ? 'concerning' : top5HolderPercent > 30 ? 'concentrated' : 'healthy',
      reasoning: `Trading volume analysis indicates ${riskScore > 60 ? 'potential wash trading patterns with circular flows detected' : riskScore > 30 ? 'a mix of organic interest and possible coordinated activity' : 'predominantly organic trading with natural price discovery'}. Holder distribution ${top5HolderPercent > 50 ? 'is heavily concentrated, creating dump risk' : top5HolderPercent > 30 ? 'shows some whale accumulation' : 'appears healthy with broad distribution'}.`,
    },

    recommendation: {
      action,
      reasoning: action === 'strong_avoid'
        ? 'Multiple high-severity risk factors present. The probability of loss significantly outweighs potential gains. Recommend complete avoidance.'
        : action === 'avoid'
        ? 'Risk-reward profile is unfavorable. While not definitive scam indicators, the combination of factors suggests caution. Look for better opportunities.'
        : action === 'hold'
        ? 'Mixed signals. If already holding, maintain position with tight stop-loss. If considering entry, wait for more clarity on holder behavior and volume patterns.'
        : action === 'buy'
        ? 'Positive risk profile with no major red flags. Consider position with standard risk management. Set stop-loss at 20-30% below entry.'
        : 'Strong fundamentals for memecoin category. Healthy metrics across the board. Still apply risk management given inherent volatility.',
      suggestedPosition: action === 'strong_avoid' || action === 'avoid' ? undefined : 'Small position (1-2% of portfolio max)',
      stopLoss: action === 'strong_avoid' || action === 'avoid' ? undefined : '25-30% below entry',
    },

    additionalInsights: [
      graduated ? 'Token has graduated from bonding curve, providing better liquidity depth.' : 'Still on bonding curve - be aware of graduation threshold and potential volatility.',
      `Market cap of $${token.marketCap.toLocaleString()} places this in the ${token.marketCap < 100000 ? 'micro-cap' : token.marketCap < 1000000 ? 'small-cap' : 'mid-cap'} category with corresponding volatility expectations.`,
      `Price is ${priceChange >= 0 ? 'up' : 'down'} ${Math.abs(priceChange).toFixed(1)}% in 24h - ${Math.abs(priceChange) > 20 ? 'high volatility period' : 'relatively stable'}.`,
    ],

    generatedAt: Date.now(),
  };
}

async function generateMockChatResponse(message: string, _tokenAddress: string): Promise<string> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 1000));

  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('buy') || lowerMessage.includes('entry')) {
    return "Based on my analysis, if you're considering an entry, I'd suggest waiting for a pullback to a support level. The current momentum might lead to a short-term correction. If you do enter now, use a small position size (1-2% of portfolio) and set a stop-loss at 25% below your entry price.";
  }

  if (lowerMessage.includes('sell') || lowerMessage.includes('exit')) {
    return "For exit strategy, consider taking partial profits in tranches rather than all at once. If you're in profit, securing 25-50% at current levels while letting the rest ride with a trailing stop could balance risk and upside potential.";
  }

  if (lowerMessage.includes('rug') || lowerMessage.includes('scam')) {
    return "I've analyzed the on-chain data for rug indicators. While I can't guarantee anything, the key factors to watch are: creator wallet movements, sudden liquidity changes, and coordinated selling from top holders. I'll flag any suspicious activity in real-time if you have alerts enabled.";
  }

  if (lowerMessage.includes('holder') || lowerMessage.includes('whale')) {
    return "Looking at the holder distribution, the top wallets appear to be a mix of early buyers and potential team wallets. I'd monitor the top 3 addresses closely - any movement of more than 5% of their holdings could signal an impending sell-off.";
  }

  return "That's a great question. Based on the current data I have access to, I'd recommend focusing on the risk score components and watching for any changes in holder behavior. Is there a specific aspect of this token you'd like me to analyze further?";
}

// ============================================================================
// Helper Functions
// ============================================================================

export function getSentimentColor(sentiment: AITeaser['sentiment']): string {
  switch (sentiment) {
    case 'bullish': return '#00ff88';
    case 'bearish': return '#ff8800';
    case 'warning': return '#ff3b3b';
    default: return '#888';
  }
}

export function getSentimentIcon(sentiment: AITeaser['sentiment']): string {
  switch (sentiment) {
    case 'bullish': return '📈';
    case 'bearish': return '📉';
    case 'warning': return '⚠️';
    default: return '➖';
  }
}

export function getActionColor(action: AIFullAnalysis['recommendation']['action']): string {
  switch (action) {
    case 'strong_buy': return '#00ff88';
    case 'buy': return '#88ff88';
    case 'hold': return '#ffb800';
    case 'avoid': return '#ff8800';
    case 'strong_avoid': return '#ff3b3b';
  }
}

export function getActionLabel(action: AIFullAnalysis['recommendation']['action']): string {
  switch (action) {
    case 'strong_buy': return 'Strong Buy';
    case 'buy': return 'Buy';
    case 'hold': return 'Hold';
    case 'avoid': return 'Avoid';
    case 'strong_avoid': return 'Strong Avoid';
  }
}
